import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/shadcn-ui/button';
import { Badge } from '@/components/shadcn-ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn-ui/tabs';
import { DataTable } from '@/components/DataTable';
import { AdminChatHistory } from '@/components/chat/AdminChatHistory';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';
import { ArrowLeft, MessageCircle } from 'lucide-react';

const formatDate = dateString => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ChatDetailView = ({ selected, onBack, identifierKey, identifierLabel }) => {
    const firstMsg = selected.chatMessages?.[0];
    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={onBack} className="border rounded-full">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight font-mono">{selected[identifierKey]}</h1>
                    <p className="text-sm text-muted-foreground">
                        {identifierLabel} · {selected.count} messages · {firstMsg ? formatDate(firstMsg.createdAt) : 'N/A'}
                    </p>
                </div>
                <div className="ml-auto">
                    <Badge variant="outline" className="rounded-md">{selected.count} messages</Badge>
                </div>
            </div>
            <div className="rounded-lg border p-4 max-h-[70vh] overflow-y-auto no-scrollbar">
                <AdminChatHistory chatHistory={selected.chatMessages} />
            </div>
        </div>
    );
};

const GuestChats = () => {
    const [activeTab, setActiveTab] = useState('guest');
    const [selectedGuest, setSelectedGuest] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);

    const [guestChats, setGuestChats] = useState([]);
    const [guestLoading, setGuestLoading] = useState(false);
    const [guestPagination, setGuestPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });

    const [userChats, setUserChats] = useState([]);
    const [userLoading, setUserLoading] = useState(false);
    const [userPagination, setUserPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });

    const fetchedTabs = useRef({ guest: false, user: false });

    const fetchGuestChats = async (page = 1) => {
        setGuestLoading(true);
        try {
            const { data } = await appAxios.get('/Admin/v1.0/get-chats-of-guest', { params: { page, limit: 10 } });
            setGuestChats(data.data.chats || []);
            setGuestPagination(data.data.pagination);
        } catch {
            toast.error('Failed to fetch guest chats');
        } finally {
            setGuestLoading(false);
        }
    };

    const fetchUserChats = async (page = 1) => {
        setUserLoading(true);
        try {
            const { data } = await appAxios.get('/Admin/v1.0/get-chats-of-users', { params: { page, limit: 10 } });
            setUserChats(data.data.chats || []);
            setUserPagination(data.data.pagination);
        } catch {
            toast.error('Failed to fetch user chats');
        } finally {
            setUserLoading(false);
        }
    };

    useEffect(() => {
        fetchGuestChats(1);
        fetchedTabs.current.guest = true;
    }, []);

    const handleTabChange = tab => {
        setActiveTab(tab);
        if (tab === 'user' && !fetchedTabs.current.user) {
            fetchUserChats(1);
            fetchedTabs.current.user = true;
        }
    };

    const guestColumns = [
        {
            key: 'guestId',
            label: 'Guest ID',
            cellClassName: 'font-mono text-sm',
            render: value => value?.slice(13) || 'N/A',
        },
        {
            key: 'count',
            label: 'Total Messages',
            render: value => <Badge variant="outline">{value ?? 0} messages</Badge>,
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_, row) => (
                <Button size="sm" variant="outline" onClick={() => setSelectedGuest(row)}>
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Chat History
                </Button>
            ),
        },
    ];

    const userColumns = [
        {
            key: 'fullname',
            label: 'Name',
            render: value => value || 'N/A',
        },
        {
            key: 'user_email',
            label: 'Email',
            render: value => value || 'N/A',
        },
        {
            key: 'count',
            label: 'Total Messages',
            render: value => <Badge variant="outline">{value ?? 0} messages</Badge>,
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_, row) => (
                <Button size="sm" variant="outline" onClick={() => setSelectedUser(row)}>
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Chat History
                </Button>
            ),
        },
    ];

    if (selectedGuest) {
        return (
            <ChatDetailView
                selected={selectedGuest}
                onBack={() => setSelectedGuest(null)}
                identifierKey="guestId"
                identifierLabel="Guest"
            />
        );
    }

    if (selectedUser) {
        return (
            <ChatDetailView
                selected={selectedUser}
                onBack={() => setSelectedUser(null)}
                identifierKey="fullname"
                identifierLabel="User"
            />
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold tracking-tight">Chats</h1>
                <p className="text-muted-foreground">View chat history from all users and guests</p>
            </div>
            <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList>
                    <TabsTrigger value="guest">Guest</TabsTrigger>
                    <TabsTrigger value="user">User</TabsTrigger>
                </TabsList>
                <TabsContent value="guest" className="mt-4">
                    <DataTable
                        data={guestChats}
                        columns={guestColumns}
                        loading={guestLoading}
                        searchable={false}
                        pagination={guestPagination}
                        onPageChange={page => fetchGuestChats(page)}
                        emptyMessage="No guest chats found"
                    />
                </TabsContent>
                <TabsContent value="user" className="mt-4">
                    <DataTable
                        data={userChats}
                        columns={userColumns}
                        loading={userLoading}
                        searchable={false}
                        pagination={userPagination}
                        onPageChange={page => fetchUserChats(page)}
                        emptyMessage="No user chats found"
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default GuestChats;
